import React, { Component, Fragment } from 'react';
import Form from 'react-validation/build/form';
import Input from 'react-validation/build/input';
import { required, isValidForm } from "../../../../util/validationMethods.js";
import { notify } from '../../../../util/notifications';
import { validateElavonCredentials } from '../../../../actions/adminAction/companyActions';

// Default payment configuration
const DEFAULT_PAYMENT_DETAILS = {
    marchantId: '',
    userId: '',
    vendorId: '',
    convergePin: '',
    keyInEntry: '',
    pinSwipe: '',
    isTestMode: false,
    isSwipeEnabled: true,
    isKeyInEnabled: true,
    requireCVV: true,
    requireAVS: false,
    allowPartialAuth: false,
    billingAddressSource: 'COMPANY'
};

class PaymentSettingInfo extends Component {
    constructor(props) {
        super(props);
        this.state = {
            validationStatus: null,
            validationMessage: '',
            isTestingConnection: false
        };
    }

    // Helper to safely get payment details with defaults
    getPaymentDetails = () => {
        const { company } = this.props;
        try {
            if (company && company.idCompany > 0 && company.paymentDetails && company.paymentDetails !== "" && company.paymentDetails !== "null") {
                return { ...DEFAULT_PAYMENT_DETAILS, ...JSON.parse(company.paymentDetails) };
            }
        } catch (e) {
            console.warn('Failed to parse paymentDetails:', e);
        }
        return { ...DEFAULT_PAYMENT_DETAILS };
    }

    onPaymentFieldChange = (e) => {
        const { name, type, checked, value } = e.target;
        var paymentDetails = this.getPaymentDetails();
        
        paymentDetails[name] = type === 'checkbox' ? checked : value;
        this.props.setPaymentInfo('paymentDetails', JSON.stringify(paymentDetails));
        this.forceUpdate();
        
        if (this.state.validationStatus) {
            this.setState({ validationStatus: null, validationMessage: '' });
        }
    }

    testElavonConnection = async () => {
        const { company } = this.props;
        
        this.formPaymentSetting.validateAll();
        if (!isValidForm(this.formPaymentSetting)) {
            if (this.props.refNotify) {
                notify.createNotification(
                    notify.notifyType.Error, 
                    'Please fill in all required fields before testing connection.', 
                    this.props.refNotify
                );
            }
            return;
        }

        this.setState({ 
            isTestingConnection: true, 
            validationStatus: 'validating',
            validationMessage: 'Testing connection to Elavon...'
        });

        try {
            const paymentDetails = this.getPaymentDetails();

            const data = await validateElavonCredentials(this.props.cid || 0, paymentDetails);

            if (data.valid === true || data.statusCode === 200) {
                this.setState({
                    validationStatus: 'success',
                    validationMessage: data.message || data.statusMsg || 'Elavon credentials validated successfully!'
                });
                if (this.props.refNotify) {
                    notify.createNotification(
                        notify.notifyType.Success,
                        data.message || data.statusMsg || 'Elavon credentials validated successfully!',
                        this.props.refNotify
                    );
                }
            } else {
                this.setState({
                    validationStatus: 'error',
                    validationMessage: data.message || data.statusMsg || 'Validation failed. Please check your credentials.'
                });
                if (this.props.refNotify) {
                    notify.createNotification(
                        notify.notifyType.Error,
                        data.message || data.statusMsg || 'Validation failed',
                        this.props.refNotify
                    );
                }
            }

        } catch (error) {
            console.error('Elavon validation error:', error);
            this.setState({
                validationStatus: 'error',
                validationMessage: 'Connection test failed. Please check your network and try again.'
            });
            if (this.props.refNotify) {
                notify.createNotification(
                    notify.notifyType.Error,
                    'Connection test failed: ' + error.message,
                    this.props.refNotify
                );
            }
        } finally {
            this.setState({ isTestingConnection: false });
        }
    }

    getValidationBadge = () => {
        const { validationStatus } = this.state;
        if (!validationStatus) return null;

        const badges = {
            validating: <span className="badge badge-info"><i className="fa fa-spinner fa-spin"></i> Validating...</span>,
            success: <span className="badge badge-success"><i className="fa fa-check"></i> Validated</span>,
            error: <span className="badge badge-danger"><i className="fa fa-times"></i> Failed</span>
        };
        return badges[validationStatus];
    }

    render() {
        const { company, canEdit, cid } = this.props;
        const paymentDetails = this.getPaymentDetails();  // Use helper method
        const { validationStatus, validationMessage, isTestingConnection } = this.state;


        return (
            <Fragment>
                <div className="pd-10">
                    <div className="row mb-10">
                        <div className="col-md-12">
                            <div className="alert alert-info">
                                <strong><i className="fa fa-info-circle"></i> Elavon Payment Gateway Configuration</strong>
                                <p className="mb-0 mt-2">
                                    Configure your Elavon merchant account credentials and payment processing rules below.
                                </p>
                            </div>
                        </div>
                    </div>

                    <Form ref={c => {
                        this.formPaymentSetting = c;
                        this.props.initCallBack(c);
                    }}>
                        <fieldset disabled={!canEdit}>
                            
                            {/* Basic Credentials */}
                            <div className="row mb-10">
                                <div className="col-md-12">
                                    <div className="card box-shadow">
                                        <div className="card-header" style={{ backgroundColor: '#00BBD8', color: 'white' }}>
                                            <h5 className="mb-0">
                                                <i className="fa fa-key"></i> Basic Credentials {this.getValidationBadge()}
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">
                                                        Merchant ID <span className="text-danger">*</span>
                                                    </label>
                                                    <Input
                                                        placeholder="Enter Merchant ID"
                                                        value={paymentDetails.marchantId || ''}
                                                        validations={[required]}
                                                        type="text"
                                                        name="marchantId"
                                                        className="form-control form-control-sm"
                                                        requiredmsg="Merchant ID is required"
                                                        onChange={this.onPaymentFieldChange}
                                                    />
                                                    <small className="form-text text-muted">Provided by Elavon</small>
                                                </div>

                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">
                                                        User ID <span className="text-danger">*</span>
                                                    </label>
                                                    <Input
                                                        placeholder="Enter User ID"
                                                        value={paymentDetails.userId || ''}
                                                        onChange={this.onPaymentFieldChange}
                                                        validations={[required]}
                                                        requiredmsg="User ID is required"
                                                        type="text"
                                                        name="userId"
                                                        className="form-control form-control-sm"
                                                    />
                                                    <small className="form-text text-muted">API user for gateway</small>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">
                                                        PIN (Key-In Entry) <span className="text-danger">*</span>
                                                    </label>
                                                    <Input
                                                        placeholder="Enter PIN for manual entry"
                                                        value={paymentDetails.keyInEntry || ''}
                                                        onChange={this.onPaymentFieldChange}
                                                        validations={[required]}
                                                        requiredmsg="Key-In PIN is required"
                                                        type="password"
                                                        name="keyInEntry"
                                                        className="form-control form-control-sm"
                                                        autoComplete="new-password"
                                                    />
                                                    <small className="form-text text-muted">For manually keyed transactions</small>
                                                </div>

                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">PIN (Swipe)</label>
                                                    <Input
                                                        placeholder="Enter PIN for card swipe"
                                                        value={paymentDetails.pinSwipe || ''}
                                                        onChange={this.onPaymentFieldChange}
                                                        type="password"
                                                        name="pinSwipe"
                                                        className="form-control form-control-sm"
                                                        autoComplete="new-password"
                                                    />
                                                    <small className="form-text text-muted">For swiped card transactions</small>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">Vendor ID</label>
                                                    <Input
                                                        placeholder="Optional Vendor ID"
                                                        value={paymentDetails.vendorId || ''}
                                                        onChange={this.onPaymentFieldChange}
                                                        type="text"
                                                        name="vendorId"
                                                        className="form-control form-control-sm"
                                                    />
                                                </div>

                                                <div className="form-group col-sm-6">
                                                    <label className="form-label col-form-label">Converge PIN</label>
                                                    <Input
                                                        placeholder="Optional Converge PIN"
                                                        value={paymentDetails.convergePin || ''}
                                                        onChange={this.onPaymentFieldChange}
                                                        type="password"
                                                        name="convergePin"
                                                        className="form-control form-control-sm"
                                                        autoComplete="new-password"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Modes */}
                            <div className="row mb-10">
                                <div className="col-md-12">
                                    <div className="card box-shadow">
                                        <div className="card-header" style={{ backgroundColor: '#00BBD8', color: 'white' }}>
                                            <h5 className="mb-0">
                                                <i className="fa fa-credit-card"></i> Payment Modes
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="isSwipeEnabled"
                                                            name="isSwipeEnabled"
                                                            checked={paymentDetails.isSwipeEnabled !== false}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="isSwipeEnabled">
                                                            <strong>Enable Swipe Payments</strong>
                                                            <small className="d-block text-muted">Allow card reader/swipe transactions</small>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="isKeyInEnabled"
                                                            name="isKeyInEnabled"
                                                            checked={paymentDetails.isKeyInEnabled !== false}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="isKeyInEnabled">
                                                            <strong>Enable Manual Entry</strong>
                                                            <small className="d-block text-muted">Allow manually keyed card numbers</small>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="isTestMode"
                                                            name="isTestMode"
                                                            checked={paymentDetails.isTestMode !== false}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="isTestMode">
                                                            <strong>Test Mode</strong>
                                                            <small className="d-block text-muted">Use Elavon sandbox (disable for live)</small>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fraud Prevention */}
                            <div className="row mb-10">
                                <div className="col-md-12">
                                    <div className="card box-shadow">
                                        <div className="card-header" style={{ backgroundColor: '#00BBD8', color: 'white' }}>
                                            <h5 className="mb-0">
                                                <i className="fa fa-shield"></i> Fraud Prevention
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="requireCVV"
                                                            name="requireCVV"
                                                            checked={paymentDetails.requireCVV !== false}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="requireCVV">
                                                            <strong>Require CVV</strong>
                                                            <small className="d-block text-muted">Mandatory CVV/CVC verification</small>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="requireAVS"
                                                            name="requireAVS"
                                                            checked={paymentDetails.requireAVS === true}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="requireAVS">
                                                            <strong>Require AVS</strong>
                                                            <small className="d-block text-muted">Validate billing address match</small>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="col-sm-4">
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="allowPartialAuth"
                                                            name="allowPartialAuth"
                                                            checked={paymentDetails.allowPartialAuth === true}
                                                            onChange={this.onPaymentFieldChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="allowPartialAuth">
                                                            <strong>Allow Partial Authorization</strong>
                                                            <small className="d-block text-muted">Accept partial card approvals</small>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Billing Address Source */}
                            <div className="row mb-10">
                                <div className="col-md-12">
                                    <div className="card box-shadow">
                                        <div className="card-header" style={{ backgroundColor: '#00BBD8', color: 'white' }}>
                                            <h5 className="mb-0">
                                                <i className="fa fa-map-marker"></i> Billing Address Settings
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-sm-12">
                                                    <label className="form-label">Billing Address Source</label>
                                                    <div className="d-flex">
                                                        <div className="custom-control custom-radio mr-4">
                                                            <input
                                                                type="radio"
                                                                className="custom-control-input"
                                                                id="billingCompany"
                                                                name="billingAddressSource"
                                                                value="COMPANY"
                                                                checked={(paymentDetails.billingAddressSource || 'COMPANY') === 'COMPANY'}
                                                                onChange={this.onPaymentFieldChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="billingCompany">
                                                                Use Company Address
                                                            </label>
                                                        </div>
                                                        <div className="custom-control custom-radio mr-4">
                                                            <input
                                                                type="radio"
                                                                className="custom-control-input"
                                                                id="billingCustomer"
                                                                name="billingAddressSource"
                                                                value="CUSTOMER"
                                                                checked={paymentDetails.billingAddressSource === 'CUSTOMER'}
                                                                onChange={this.onPaymentFieldChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="billingCustomer">
                                                                Use Customer Address
                                                            </label>
                                                        </div>
                                                        <div className="custom-control custom-radio">
                                                            <input
                                                                type="radio"
                                                                className="custom-control-input"
                                                                id="billingManual"
                                                                name="billingAddressSource"
                                                                value="MANUAL"
                                                                checked={paymentDetails.billingAddressSource === 'MANUAL'}
                                                                onChange={this.onPaymentFieldChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="billingManual">
                                                                Manual Entry at Payment
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <small className="form-text text-muted">
                                                        Determines where billing address is pulled from for AVS verification
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Validation Status */}
                            {validationMessage && (
                                <div className="row mb-10">
                                    <div className="col-md-12">
                                        <div className={`alert ${validationStatus === 'success' ? 'alert-success' : validationStatus === 'validating' ? 'alert-info' : 'alert-danger'}`}>
                                            <i className={`fa ${validationStatus === 'success' ? 'fa-check-circle' : validationStatus === 'validating' ? 'fa-spinner fa-spin' : 'fa-exclamation-circle'}`}></i>
                                            {' '}{validationMessage}
                                        </div>
                                    </div>
                                </div>
                            )}

                        </fieldset>
                    </Form>

                    {/* Action Buttons */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="btn-group float-right">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-info"
                                    onClick={this.testElavonConnection}
                                    disabled={!canEdit || isTestingConnection}
                                >
                                    {isTestingConnection ? (
                                        <><i className="fa fa-spinner fa-spin"></i> Testing...</>
                                    ) : (
                                        <><i className="fa fa-plug"></i> Test Connection</>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.formPaymentSetting.validateAll();
                                        if (!isValidForm(this.formPaymentSetting)) {
                                            if (this.props.refNotify) {
                                                notify.createNotification(
                                                    notify.notifyType.Error,
                                                    'Please fill in all required fields',
                                                    this.props.refNotify
                                                );
                                            }
                                        } else {
                                            this.props.onSaveCompany();
                                        }
                                    }}
                                    disabled={!canEdit}
                                >
                                    <i className="fa fa-save"></i> SAVE COMPANY
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Security Notice */}
                    <div className="row mt-3">
                        <div className="col-md-12">
                            <div className="alert alert-warning">
                                <strong><i className="fa fa-lock"></i> Security Notice</strong>
                                <p className="mb-0 mt-2 small">
                                    All payment credentials are encrypted using AES-256 encryption.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default PaymentSettingInfo;